/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

// Utilities for acting on 'streams', where a stream is simply a Promise
// which resolves to a 'value' of the stream, plus a Promise to the rest
// of the stream. (Definition is recursive)

// I love how beautifully these definitions fall into place
// A somewhat similar but more complicated approach is documented here: https://medium.com/@yelouafi/promises-fp-beautiful-streams-6f0235c5b179

// One issue is that promises are eagerly evaluated. We could get around this by making the 'next' attribute a function that returns a
// Promise (possibly lazily) instead of an actual Promise. Then you could have, for example, an infinite sequence represented by a stream.
// We would have to say then that a Stream is a function that returns a Promise.

function map(stream, mapper) {
	return stream.then(function(data) {
		return {
			value: data.value !== undefined ? mapper(data.value) : undefined,
			next: data.next ? map(data.next, mapper) : undefined
		};
	});
}

function filter(stream, pred) {
	return stream.then(function(data) {
		var next = data.next ? filter(data.next, pred) : {};

		return pred(data.value) ?
			{
				value: data.value,
				next: next
			}
			: next;
	});
}

function reduce(stream, fn, memo) {
	return stream.then(function(data) {
		memo = data.value !== undefined ? fn(memo, data.value) : memo;
		return data.next ? reduce(data.next, fn, memo) : memo;
	});
}

function toArray(stream) {
	return reduce(stream, function(m, v) { return m.concat([v]); }, []);
}

function fromArray(arr) {
	return Promise.resolve({
		value: arr.length > 0 ? arr[0] : undefined,
		next: arr.length > 1 ? fromArray(arr.slice(1)) : undefined
	});
}

function concat(s1, s2) {
	return s1.then(function(data) {
		return {
			value: data.value,
			next: data.next ? concat(data.next, s2) : s2
		};
	});
}

function each(stream, fn) {
	return stream.then(function(data) {
		if(data.value !== undefined) {
			fn(data.value);
		}
		if(data.next) {
			return each(data.next, fn);
		}
	});
}

function split(stream, separator) {
	return _split(stream, separator, "");
}

function _split(stream, separator, str) {
	return stream.then(function(data) {
		if(data.value) {
			str += data.value;
		}

		if(data.next) {
			var items = str.split(separator);
			if(items.length > 1) {
				str = items[items.length-1];
				return concat(
					fromArray(items.slice(0, items.length - 1)),
					_split(data.next, separator, str)
				);
			} else {
				return _split(data.next, separator, str);
			}
		} else {
			return fromArray(str.split(separator));
		}
	});
}

function fromEventEmitter(fn /*(next,finished,error)*/) {
	var resolve, reject;

	function nextPromise() {
		return new Promise(function(r, j) {
			resolve = r;
			reject = j;
		})
	};

	function next(value) {
		resolve({
			value: value,
			next: nextPromise()
		});
	}

	function finished() {
		resolve({});
	}

	function error(err) {
		reject(err); // Note, reject changes every call to nextPromise
	}

	fn(next, finished, error);

	return nextPromise();
}

export default {
	map, filter, toArray, fromArray, reduce, concat, split, each, fromEventEmitter
};
